import ListActions from "modules/list/actions";
import { AuditSchema } from "utils/schemas";
import api from "services/api";
import store from "services/store";
import { getCurrentContext } from "state/auth/selectors/common";
import moment from "moment";
import AsyncAction from "modules/asyncAction";
import { downloadFile, convertToCSV, formatDate } from "utils/links";
import notifications from "services/notifications";
import i18next from "i18next";

import { auditFetcher, modalService, resourceNamesFetcher } from "./services";
import { resourceNamesPoller } from "utils/tasks";

export const downloadCsvReportAsyncAction = new AsyncAction({
  promise: () => {
    const query = store.getState()?.list?.auditlogs?.query;
    return store.dispatch(downloadReport(query));
  },
});

function csvFilename(query) {
  const [startTime, endTime] = query?.rangePicker || [];
  return `audit_logs_${formatDate(startTime)}_${formatDate(endTime)}.csv`;
}

const downloadReport = (query) => {
  return async (dispatch) => {
    const promise = fetchLogs(query);
    dispatch({
      type: "FETCH_ALL_AUDIT_LOGS",
      promise,
    });

    try {
      const items = await promise;
      downloadFile({
        content: convertToCSV(items),
        fileName: csvFilename(query),
      });
    } catch (err) {
      notifications.error({
        message: i18next.t(
          "Something went wrong while trying to download the report."
        ),
      });
    }
  };
};

const fetchLogs = async (query, items = []) => {
  const response = await fetchAuditLogs(query);
  const fetchedItems = response?.items || [];
  const continueToken = response?.listmeta?.continue;
  const logCollection = [...items, ...fetchedItems];
  const newQuery = { ...query, continue: continueToken };
  if (continueToken) {
    return await fetchLogs(newQuery, logCollection);
  }

  return logCollection;
};

function fetchAuditLogs(query) {
  const currentContext = getCurrentContext(store.getState());
  const [startTime, endTime] = query.rangePicker || [];

  let filters = {
    continue: query.continue,
    startTime: moment(startTime).startOf("day").format(),
    endTime: moment(endTime).endOf("day").format(),
    ...(query.actionType !== "all" && { actionType: query.actionType }),
    ...(query.userUid !== "all" && { userUid: query.userUid }),
    ...(query.resourceUid !== "all" && { resourceUid: query.resourceUid }),
    ...(query.resourceKind !== "all" && { resourceKind: query.resourceKind }),
    ...(query.projectUid &&
      query.projectUid !== "all" && { projectUid: query.projectUid }),
  };

  if (!currentContext.isAdmin) {
    filters = { ...filters, projectUid: currentContext.projectUid };
  }

  return api.get(`v1/audits`, filters);
}

export const auditListActions = new ListActions({
  schema: [AuditSchema],
  fetchData(query) {
    return fetchAuditLogs(query);
  },
  initialQuery() {
    return {
      resourceKind: "all",
      projectUid: "all",
      resourceUid: "all",
      actionType: "all",
      userUid: "all",
      rangePicker: [moment().subtract(1, "months"), moment()],
      limit: 50,
    };
  },
});

function fetchAllResourcesNames(resourceKind) {
  return async function thunk(dispatch) {
    dispatch(resourceNamesFetcher.key(resourceKind).flushCache());
    resourceNamesPoller.start();
    dispatch({
      type: "FETCH_RESOURCE_NAMES",
      resources: [],
    });
  };
}

export function openAuditDetailsModal(auditUid) {
  return function thunk(dispatch) {
    modalService.open({ auditUid });
    dispatch(auditFetcher.fetch());
  };
}

export function onFiltersChange(name, value) {
  return function thunk(dispatch, getState) {
    const module = "auditlogs";

    dispatch(auditListActions.changeQuery({ name, value, module }));

    const state = getState();
    const currentContext = getCurrentContext(state);
    const projectId = currentContext.isAdmin
      ? state.list[module].query.projectUid
      : currentContext.projectUid;
    const resourceKind = state.list[module].query.resourceKind;

    if (projectId !== "all" && resourceKind !== "all") {
      dispatch(
        auditListActions.changeQuery({
          name: "resourceUid",
          value: "all",
          module,
        })
      );

      return dispatch(fetchAllResourcesNames(resourceKind));
    }

    dispatch({
      type: "DISPLAY_RESOURCE_NAMES_DROPDOWN",
      value: false,
    });
    dispatch(
      auditListActions.changeQuery({
        name: "resourceUid",
        value: "all",
        module,
      })
    );
  };
}
